
import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, ExternalLink } from 'lucide-react';
import { Game, ChatMessage } from '../../../types';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Card from '../../../components/base/Card';

interface ChatAreaProps {
  selectedGame?: Game;
  onSourceClick: (sources: any[]) => void;
}

export default function ChatArea({ selectedGame, onSourceClick }: ChatAreaProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const quickQuestions = [
    'ゲーム開始の準備は？',
    '得点計算について教えて',
    'VRCのつなぎ方を教えて'
  ];
  
  useEffect(() => {
    if (selectedGame) {
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        type: 'ai',
        content: selectedGame.id.startsWith('demo-') 
          ? 'こちらはデモゲームです。実際のゲーム情報については、りゅうたま、カタン、スプレンダーなど他のゲームをお選びください。'
          : `${selectedGame.name}のルールについて何が知りたいですか？`,
        timestamp: new Date().toISOString()
      };
      setMessages([welcomeMessage]);
    } else {
      setMessages([]);
    }
  }, [selectedGame]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleSendMessage = async (content: string) => {
    if (!content.trim() || !selectedGame) return;
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    // AIレスポンスのシミュレーション
    setTimeout(() => {
      const aiResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: generateAIResponse(content, selectedGame),
        timestamp: new Date().toISOString(),
        sources: selectedGame.id.startsWith('demo-') ? [] : selectedGame.sources
      };
      
      setMessages(prev => [...prev, aiResponse]);
      setIsLoading(false);
    }, 1500);
  };
  
  const generateAIResponse = (question: string, game: Game): string => {
    // デモゲームの場合は定型文を返す
    if (game.id.startsWith('demo-')) {
      return 'こちらはデモゲームのため、詳細な情報は提供できません。実際のゲーム情報については、りゅうたま、カタン、スプレンダーなど他のゲームをお選びください。ご理解のほどよろしくお願いいたします。';
    }
    
    const lowerQuestion = question.toLowerCase();
    
    if (lowerQuestion.includes('準備') || lowerQuestion.includes('開始')) {
      const faq = game.faq.find(f => f.question.includes('準備'));
      return faq?.answer || `${game.name}のゲーム準備について説明します。まず必要な道具を揃えて、プレイヤー人数を確認してください。`;
    }
    
    if (lowerQuestion.includes('同点') || lowerQuestion.includes('勝敗')) {
      const faq = game.faq.find(f => f.question.includes('同点'));
      return faq?.answer || `${game.name}では同点の場合の勝敗判定ルールがあります。詳細はルールブックをご確認ください。`;
    }
    
    if (lowerQuestion.includes('vrc') || lowerQuestion.includes('つなぎ方')) {
      const faq = game.faq.find(f => f.question.includes('VRC'));
      return faq?.answer || `VRChatで${game.name}を楽しむには、まずVRChatアプリを起動してワールドを検索してください。`;
    }
    
    if (lowerQuestion.includes('得点') || lowerQuestion.includes('計算')) {
      return `${game.name}の得点計算について説明します。基本的なルールに従って点数を計算し、最終的な勝者を決定します。`;
    }
    
    // わからない場合の回答
    return `申し訳ございませんが、「${question}」についてはわかりません。より詳しい情報については、カスタマーサポートまでお問い合わせください。`;
  };
  
  const handleQuickQuestion = (question: string) => {
    handleSendMessage(question);
  };
  
  if (!selectedGame) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <Bot className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium mb-2">ゲームを選ぶと、ここにチャットが表示されます</p>
          <p className="text-sm">左側からゲームを選択してください</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col">
      {/* ヘッダー */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">{selectedGame.name}</h3>
          {selectedGame.id.startsWith('demo-') && (
            <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              DEMO
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500">
          {selectedGame.id.startsWith('demo-') 
            ? 'デモゲーム - 実際の情報ではありません' 
            : `参照元: ${selectedGame.sources[0]?.name}`
          }
        </p>
      </div>
      
      {/* メッセージエリア */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`flex items-start space-x-2 max-w-xs lg:max-w-md ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${message.type === 'user' ? 'bg-blue-600' : 'bg-gray-200'}`}>
                {message.type === 'user' ? (
                  <User className="h-4 w-4 text-white" />
                ) : (
                  <Bot className="h-4 w-4 text-gray-600" />
                )}
              </div>
              <div className={`rounded-lg p-3 ${message.type === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200'}`}>
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                {message.sources && message.sources.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => onSourceClick(message.sources!)}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span>出典: {message.sources[0].page}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-start space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <Bot className="h-4 w-4 text-gray-600" />
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* クイック質問 */}
      {messages.length <= 1 && !selectedGame.id.startsWith('demo-') && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 mb-2">よくある質問:</p>
          <div className="flex flex-wrap gap-2">
            {quickQuestions.map((question, index) => (
              <Button
                key={index}
                variant="ghost"
                size="sm"
                onClick={() => handleQuickQuestion(question)}
                className="text-xs"
              >
                {question}
              </Button>
            ))}
          </div>
        </div>
      )}
      
      {/* 入力エリア */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <Input
            placeholder={selectedGame.id.startsWith('demo-') ? "デモゲームです" : "例：同点のとき勝敗は？"}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(inputValue);
              }
            }}
            className="flex-1"
            disabled={selectedGame.id.startsWith('demo-')}
          />
          <Button
            onClick={() => handleSendMessage(inputValue)}
            disabled={!inputValue.trim() || isLoading || selectedGame.id.startsWith('demo-')}
            size="md"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        
        {/* CSエスカレーション */}
        <div className="mt-2 text-center">
          <p className="text-xs text-gray-500">
            困難度の高い個別裁定は
            <button 
              className="text-blue-600 hover:text-blue-800 ml-1"
              onClick={() => window.REACT_APP_NAVIGATE('/support')}
            >
              カスタマーサポートに相談する
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
